import type { T } from '../../../messages';

/**
 * **What is next for this business**, from what the businesses list already
 * has — one line per row (console redesign, phase B6; the mockup's "Next"
 * column).
 *
 * A pure function of three reads the list makes anyway: the fleet row (its
 * stage), the intake chip (is a questionnaire out with the owner) and the page's
 * health (is anything wrong). The readiness read phase B1 builds
 * (`GET /tenants/:id/readiness`) will know more — staff logins, hours, the
 * website — and when the list can afford it per row, `nextStepOf` is the one
 * function to switch; the words (`nextLine`) stay.
 *
 * The order is the operator's: something **wrong** comes first whatever the
 * stage, because a live business whose partner stopped hearing about bookings
 * is losing them now; then whose move it is (the owner's questionnaire, or
 * ours); then "nothing to do". An offboarded business has nothing next, even
 * if a stale queue says otherwise.
 */

export interface NextInput {
  status: string;
  /** The live intake, if there is one (the fleet's chip). */
  intake?: { status: string; answered: number; total: number } | undefined;
  /** The page's health read for this row, once it has landed. */
  health?: { verdict: string; reasons: readonly string[] } | undefined;
  /** The fleet row's own flag — known before health is. */
  webhookFailing?: boolean | undefined;
}

export type NextStep =
  | { kind: 'fix'; reasons: readonly string[] }
  | { kind: 'waiting-owner'; answered: number; total: number }
  | { kind: 'review-intake' }
  | { kind: 'setting-up' }
  | { kind: 'importing' }
  | { kind: 'paused' }
  | { kind: 'offboarded' }
  | { kind: 'checking' }
  | { kind: 'nothing' };

export function nextStepOf(input: NextInput): NextStep {
  if (input.status === 'offboarded') return { kind: 'offboarded' };

  if (input.health !== undefined && input.health.verdict !== 'quiet') {
    const reasons =
      input.health.reasons.length > 0
        ? input.health.reasons
        : input.webhookFailing === true
          ? ['endpoint_failing']
          : [];
    return { kind: 'fix', reasons };
  }
  if (input.health === undefined && input.webhookFailing === true) {
    return { kind: 'fix', reasons: ['endpoint_failing'] };
  }

  switch (input.status) {
    case 'paused':
      return { kind: 'paused' };
    case 'onboarding':
      if (input.intake?.status === 'submitted') return { kind: 'review-intake' };
      if (input.intake !== undefined && input.intake.status !== 'closed') {
        return {
          kind: 'waiting-owner',
          answered: input.intake.answered,
          total: input.intake.total,
        };
      }
      return { kind: 'setting-up' };
    case 'importing':
      return input.intake?.status === 'submitted'
        ? { kind: 'review-intake' }
        : { kind: 'importing' };
    case 'live':
      // Live and nothing flagged yet: say so only once health has answered.
      return input.health === undefined ? { kind: 'checking' } : { kind: 'nothing' };
    default:
      return { kind: 'nothing' };
  }
}

/** The line: a bold lead ("Fix:", "Waiting on the owner") and the rest, either may be empty. */
export interface NextLine {
  lead: string;
  rest: string;
}

export function nextLine(t: T, step: NextStep): NextLine {
  switch (step.kind) {
    case 'fix': {
      const [first, ...others] = step.reasons;
      if (first === undefined) return { lead: t('businesses.next.fix'), rest: '' };
      return {
        lead: t('businesses.next.fix'),
        rest:
          reasonLabel(t, first) +
          (others.length > 0 ? t('businesses.next.more', { count: others.length }) : ''),
      };
    }
    case 'waiting-owner':
      return {
        lead: t('businesses.next.waiting'),
        rest: t('businesses.next.questionnaire', { answered: step.answered, total: step.total }),
      };
    case 'review-intake':
      return { lead: t('businesses.next.review'), rest: t('businesses.next.reviewWhat') };
    case 'setting-up':
      return { lead: t('businesses.next.setup'), rest: t('businesses.next.setupWhat') };
    case 'importing':
      return { lead: t('businesses.next.importing'), rest: t('businesses.next.importingWhat') };
    case 'paused':
      return { lead: t('businesses.next.paused'), rest: t('businesses.next.pausedWhat') };
    case 'offboarded':
      return { lead: '', rest: t('businesses.next.offboarded') };
    case 'checking':
      return { lead: '', rest: t('businesses.next.checking') };
    case 'nothing':
      return { lead: '', rest: t('businesses.next.nothing') };
  }
}

/**
 * A health reason in the owner's terms — "booking updates to a partner stopped
 * arriving" rather than "a webhook delivery gave up". The server names the
 * reason (`repos/health.ts`); one a newer server sends that is not here is
 * shown as its token with spaces, rather than as nothing.
 */
export function reasonLabel(t: T, reason: string): string {
  switch (reason) {
    case 'webhook_exhausted':
      return t('businesses.reason.webhook_exhausted');
    case 'endpoint_failing':
      return t('businesses.reason.endpoint_failing');
    case 'webhook_failed':
      return t('businesses.reason.webhook_failed');
    case 'webhook_stuck':
      return t('businesses.reason.webhook_stuck');
    case 'text_exhausted':
      return t('businesses.reason.text_exhausted');
    case 'text_failed':
      return t('businesses.reason.text_failed');
    case 'text_stuck':
      return t('businesses.reason.text_stuck');
    case 'request_overdue':
      return t('businesses.reason.request_overdue');
    case 'site_not_served':
      return t('businesses.reason.site_not_served');
    default:
      return reason.replace(/_/g, ' ');
  }
}
